import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	DocumentTextIcon,
	ExclamationTriangleIcon,
	PrinterIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Button, ConfirmDialog } from '../../components/ui';
import useAuthStore from '../../stores/authStore';
import { AGREEMENT_SECTIONS } from './agreementContent';

const PolicyAgreement = () => {
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const [confirmRead, setConfirmRead] = useState(false);
	const [confirm1099, setConfirm1099] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);

	const isSubmitEnabled = confirmRead && confirm1099;
	const staffName = user?.name || 'Staff Member';

	const handleSubmit = () => {
		if (!isSubmitEnabled) return;
		setShowSuccess(true);
	};

	const handleContinue = () => {
		setShowSuccess(false);
		navigate('/staff/dashboard');
	};

	const handlePrint = () => {
		window.print();
	};

	return (
		<div className="staff-agreement">
			{/* Header */}
			<header className="staff-agreement__header">
				<div className="staff-agreement__header-logo">
					<DocumentTextIcon className="w-7 h-7 text-indigo-600" />
				</div>
				<div>
					<h1 className="staff-agreement__header-title">Beauty Biz Portal</h1>
					<p className="staff-agreement__header-subtitle">Staff Agreement Portal</p>
				</div>
			</header>

			{/* Main Container */}
			<main className="staff-agreement__main">
				{/* Welcome Card */}
				<div className="staff-agreement__welcome">
					<h2 className="staff-agreement__welcome-title">
						Welcome, <span className="text-indigo-500 font-semibold">{staffName}</span>
					</h2>
					<p className="staff-agreement__welcome-text">
						Before you can start working, please review and accept the required
						agreements below. This is a one-time process.
					</p>
				</div>

				{/* Pending Notice */}
				<div className="staff-agreement__pending">
					<ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
					<div>
						<strong className="block text-amber-800">Action Required</strong>
						<span className="text-amber-700 text-sm">
							You have pending agreements to review and accept.
						</span>
					</div>
				</div>

				{/* Agreement Card */}
				<div className="staff-agreement__card">
					{/* Agreement Header */}
					<div className="staff-agreement__card-header">
						<div className="flex items-center gap-3.5">
							<div className="staff-agreement__card-icon">
								<DocumentTextIcon className="w-6 h-6 text-indigo-600" />
							</div>
							<div>
								<h3 className="font-semibold text-lg">
									1099 & Staff Policy Agreement
								</h3>
								<p className="text-sm opacity-90 mt-0.5">
									Staff Conduct & Compliance
								</p>
							</div>
						</div>
						<span className="staff-agreement__status staff-agreement__status--pending">
							Pending
						</span>
					</div>

					{/* Agreement Content */}
					<div className="staff-agreement__content">
						<div className="mb-5">
							<h4 className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
								<DocumentTextIcon className="w-5 h-5 text-indigo-500" />
								Agreement Summary
							</h4>
							<p className="text-sm text-gray-500 leading-relaxed">
								This agreement sets expectations for{' '}
								<strong className="text-gray-700">professionalism</strong>,{' '}
								<strong className="text-gray-700">quality</strong>,{' '}
								<strong className="text-gray-700">accountability</strong>, and{' '}
								<strong className="text-gray-700">1099 tax status</strong>.
							</p>
						</div>

						{/* Sections */}
						{AGREEMENT_SECTIONS.map(section => (
							<div key={section.number} className="staff-agreement__section">
								<div className="staff-agreement__section-header">
									<span className="staff-agreement__section-title">
										{section.number}. {section.title}
									</span>
									<span
										className={`staff-agreement__badge ${section.badgeClass}`}
									>
										{section.badge}
									</span>
								</div>
								<div className="staff-agreement__section-content">
									{section.content}
								</div>
							</div>
						))}
					</div>

					{/* Confirmation Checkboxes */}
					<div className="staff-agreement__confirmation">
						<div className="flex flex-col gap-3">
							<label
								className={`staff-agreement__checkbox-item ${
									confirmRead ? 'staff-agreement__checkbox-item--checked' : ''
								}`}
							>
								<input
									type="checkbox"
									checked={confirmRead}
									onChange={e => setConfirmRead(e.target.checked)}
									className="staff-agreement__checkbox"
								/>
								<div>
									<strong className="block text-sm text-gray-800">
										I confirm I read and understand this agreement.
									</strong>
									<span className="block text-xs text-gray-500 mt-0.5">
										Quality, punctuality, conduct, and rules apply at all times.
									</span>
								</div>
							</label>

							<label
								className={`staff-agreement__checkbox-item ${
									confirm1099 ? 'staff-agreement__checkbox-item--checked' : ''
								}`}
							>
								<input
									type="checkbox"
									checked={confirm1099}
									onChange={e => setConfirm1099(e.target.checked)}
									className="staff-agreement__checkbox"
								/>
								<div>
									<strong className="block text-sm text-gray-800">
										I acknowledge 1099 status and tax responsibility.
									</strong>
									<span className="block text-xs text-gray-500 mt-0.5">
										No taxes withheld; I am responsible for paying my own taxes.
									</span>
								</div>
							</label>
						</div>
					</div>

					{/* Footer Actions */}
					<div className="staff-agreement__footer">
						<span className="text-xs text-gray-400">
							&copy; 2025 Beauty Business Portal. All rights reserved.
						</span>
						<div className="flex gap-2.5">
							<Button variant="secondary" onClick={handlePrint}>
								<PrinterIcon className="w-4 h-4 mr-1.5" />
								Print
							</Button>
							<Button
								variant="primary"
								disabled={!isSubmitEnabled}
								onClick={handleSubmit}
							>
								<CheckIcon className="w-4 h-4 mr-1.5" />
								Agree & Submit
							</Button>
						</div>
					</div>
				</div>
			</main>

			{/* Success Modal */}
			<ConfirmDialog
				isOpen={showSuccess}
				onClose={handleContinue}
				onConfirm={handleContinue}
				title="Agreement Accepted!"
				message="Thank you for accepting the 1099 & Staff Policy Agreement. You can now access the full application."
				confirmLabel="Continue"
				variant="primary"
				showCancel={false}
				icon={<CheckCircleIcon className="w-5 h-5 text-green-600" />}
			/>
		</div>
	);
};

export default PolicyAgreement;
